<script setup lang="ts">
import { computed } from 'vue'
import type { Component } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

import {
  ChevronDownIcon,
  MoreDots,
  LayoutDashboardIcon,
  ChartLineIcon,
  UsersGroupIcon,
  CommentIcon,
  FileWaveIcon,
  ListIcon,
  CircleQuestionIcon,
  IndianRupeeIcon,
  FileSearchIcon,
  BanIcon,
  // SettingsIcon,
  // BarChartIcon,
  // TemplateIcon,
  NewsPaperIcon,
  DoNotEnterIcon,
  ClockIcon,
  BoxCubeIcon,
  RightLeftLargeIcon,
  InProgressIcon,
  SquareCheckIcon,
  WonIcon,
  FileCheckIcon
} from '../../icons'
import { useSidebar } from '@/composables/useSidebar'

const route = useRoute()
const authStore = useAuthStore()
const { isExpanded, isMobileOpen, isHovered, openSubmenu } = useSidebar()

// FIX: Change from role to userType
const userType = computed(() => authStore.user?.userType || '')

interface MenuItem {
  icon: Component
  name: string
  path?: string
  userTypes?: string[] // Change from roles to userTypes
  subItems?: Array<{
    name: string
    path: string
    userTypes?: string[] // Change from roles to userTypes
    new?: boolean
    pro?: boolean
  }>
}

interface MenuGroup {
  title: string
  userTypes?: string[] // Change from roles to userTypes
  items: MenuItem[]
}

// Helper function to check if user has access to menu item
const hasAccess = (userTypes?: string[]) => {
  if (!userTypes || userTypes.length === 0) return true
  return userTypes.includes(userType.value)
}

// All menu groups with user type-based access
const menuGroups: MenuGroup[] = [
  {
    title: '',
    userTypes: ['admin', 'agent', 'engineer', 'client'],
    items: [
      {
        icon: LayoutDashboardIcon,
        name: 'Dashboard',
        path: '/dashboard',
        userTypes: ['admin', 'agent', 'engineer', 'client']
      },
    ]
  },

  // ADMIN MENU GROUP (only for admin)
  {
    title: '',
    userTypes: ['admin'],
    items: [
      {
        icon: ListIcon,
        name: 'Tenders List',
        path: '/tenders',
        userTypes: ['admin']
      },
      {
        icon: ChartLineIcon,
        name: 'Manage Tenders',
        userTypes: ['admin'],
        subItems: [
          // {
          //   name: 'Unassigned Tender',
          //   path: '/unassigned-tenders',
          //   userTypes: ['admin']
          // },
          {
            name: 'Watchlist Tender',
            path: '/watchlist-tenders',
            userTypes: ['admin']
          },
          {
            name: 'Assigned Tender',
            path: '/assigned-tenders-admin',
            userTypes: ['admin']
          },
          {
            name: 'InProgress Tender',
            path: '/in-progress-admin',
            userTypes: ['admin']
          },
          {
            name: 'Price Review',
            path: '/price-review-admin',
            userTypes: ['admin']
          },
          {
            name: 'Criteria Review',
            path: '/criteria-review-admin',
            userTypes: ['admin']
          },
          {
            name: 'Ready to Bid',
            path: '/ready-to-bid-admin',
            userTypes: ['admin']
          },
          {
            name: 'Not Fit',
            path: '/not-fit-admin',
            userTypes: ['admin']
          },
          {
            name: 'Not Bidded',
            path: '/not-bidded-admin',
            userTypes: ['admin']
          },
          {
            name: 'Submitted',
            path: '/submitted-tender-admin',
            userTypes: ['admin']
          },
          {
            name: 'Under Query',
            path: '/under-query-admin',
            userTypes: ['admin']
          },
          {
            name: 'Won Tenders',
            path: '/won-tenders',
            userTypes: ['admin']
          },
          {
            name: 'Lost Tenders',
            path: '/lost-tenders',
            userTypes: ['admin']
          },
          {
            name: 'Engineer Tender',
            path: '/engineer-tenders',
            userTypes: ['admin']
          },
          {
            name: 'Completed Tenders',
            path: '/completed-tenders',
            userTypes: ['admin']
          },
          {
            name: 'Rejected Tender',
            path: '/rejected-tenders',
            userTypes: ['admin']
          },
        ],
      },
      {
        icon: FileWaveIcon,
        name: 'Manage Documents',
        path: '/media-management',
        userTypes: ['admin']
      },
      // {
      //   icon: TemplateIcon,
      //   name: 'Manage Templates',
      //   path: '/template-management',
      //   userTypes: ['admin']
      // },
      {
        icon: UsersGroupIcon,
        name: 'Manage Users',
        path: '/manage-users',
        userTypes: ['admin']
      },
      {
        icon: NewsPaperIcon,
        name: 'Manage Reports',
        path: '/manage-reports',
        userTypes: ['admin']
      },
      {
        icon: BoxCubeIcon,
        name: 'Installed Assets',
        path: '/admin/assets',
        userTypes: ['admin']
      },
      // {
      //   icon: CommentIcon,
      //   name: 'Client Feedback',
      //   path: '/manage-feedback',
      //   userTypes: ['admin']
      // },
      // {
      //   icon: BarChartIcon,
      //   name: 'Reports & Analytics',
      //   path: '/admin/reports',
      //   userTypes: ['admin']
      // },
      // {
      //   icon: SettingsIcon,
      //   name: 'System Settings',
      //   path: '/admin/settings',
      //   userTypes: ['admin']
      // },
    ]
  },

  // AGENT MENU GROUP
  {
    title: '',
    userTypes: ['agent'],
    items: [
      {
        icon: SquareCheckIcon,
        name: 'Assigned Tenders',
        path: '/assigned-tenders-agent',
        userTypes: ['agent']
      },
      {
        icon: InProgressIcon,
        name: 'In Progress',
        path: '/in-progress-agent',
        userTypes: ['agent']
      },
      {
        icon: IndianRupeeIcon,
        name: 'Price Review',
        path: '/price-review-agent',
        userTypes: ['agent']
      },
      {
        icon: FileSearchIcon,
        name: 'Criteria Review',
        path: '/criteria-review-agent',
        userTypes: ['agent']
      },
      {
        icon: RightLeftLargeIcon,
        name: 'Ready To Bid',
        path: '/ready-to-bid-agent',
        userTypes: ['agent']
      },
      {
        icon: DoNotEnterIcon,
        name: 'Not Fit',
        path: '/not-fit-agent',
        userTypes: ['agent']
      },
      {
        icon: ClockIcon,
        name: 'Not Bidded',
        path: '/not-bidded-agent',
        userTypes: ['agent']
      },
      {
        icon: FileSearchIcon,
        name: 'Submitted Tender',
        path: '/submitted-tender-agent',
        userTypes: ['agent']
      },
      {
        icon: CircleQuestionIcon,
        name: 'Under Query Tender',
        path: '/under-query-agent',
        userTypes: ['agent']
      },
      {
        icon: WonIcon,
        name: 'Won Tender',
        path: '/won-tender-agent',
        userTypes: ['agent']
      },
      {
        icon: BanIcon,
        name: 'Lost Tender',
        path: '/lost-tender-agent',
        userTypes: ['agent']
      },
      {
        icon: FileCheckIcon,
        name: 'Completed Tender',
        path: '/completed-tender-agent',
        userTypes: ['agent']
      },
      // {
      //   icon: TemplateIcon,
      //   name: 'Manage Templates',
      //   path: '/template-management',
      //   userTypes: ['agent']
      // },
    ]
  },

  // ENGINEER MENU GROUP
  {
    title: '',
    userTypes: ['engineer'],
    items: [
      {
        icon: UsersGroupIcon,
        name: 'Completed Projects',
        path: '/completed-projects',
        userTypes: ['engineer'],
      },
      {
        icon: UsersGroupIcon,
        name: 'Resource Management',
        path: '/resource-management',
        userTypes: ['engineer']
      },
      {
        icon: CommentIcon,
        name: 'Client Feedback',
        path: '/client-feedback',
        userTypes: ['engineer']
      },
      {
        icon: NewsPaperIcon,
        name: 'Manage Reports',
        path: '/manage-reports',
        userTypes: ['engineer']
      },
    ]
  },

  // CLIENT MENU GROUP
  {
    title: '',
    userTypes: ['client'],
    items: [
      {
        icon: UsersGroupIcon,
        name: 'My Projects',
        path: '/client/projects',
        userTypes: ['client']
      },
      {
        icon: UsersGroupIcon,
        name: 'Communication',
        path: '/client/chat',
        userTypes: ['client']
      },
      {
        icon: UsersGroupIcon,
        name: 'Documents',
        path: '/client/documents',
        userTypes: ['client']
      },
      {
        icon: UsersGroupIcon,
        name: 'Support Tickets',
        path: '/client/support',
        userTypes: ['client']
      },
      {
        icon: UsersGroupIcon,
        name: 'Submit Feedback',
        path: '/client/feedback',
        userTypes: ['client']
      },
    ]
  }
]

const isActive = (path: string) => route.path === path

const toggleSubmenu = (groupIndex: number, itemIndex: number) => {
  const key = `${groupIndex}-${itemIndex}`
  openSubmenu.value = openSubmenu.value === key ? null : key
}

const isAnySubmenuRouteActive = computed(() => {
  return menuGroups.some((group) =>
    hasAccess(group.userTypes) && group.items.some(
      (item) => hasAccess(item.userTypes) && item.subItems && item.subItems.some((subItem) =>
        hasAccess(subItem.userTypes) && isActive(subItem.path)
      ),
    ),
  )
})

const isSubmenuOpen = (groupIndex: number, itemIndex: number) => {
  const key = `${groupIndex}-${itemIndex}`
  const group = menuGroups[groupIndex]
  const item = group.items[itemIndex]

  if (!hasAccess(group.userTypes) || !hasAccess(item.userTypes)) return false

  return (
    openSubmenu.value === key ||
    (isAnySubmenuRouteActive.value &&
      item.subItems?.some((subItem) =>
        hasAccess(subItem.userTypes) && isActive(subItem.path)
      ))
  )
}

// Filter menu groups based on user type
const filteredMenuGroups = computed(() => {
  return menuGroups.filter(group => hasAccess(group.userTypes))
})

// Filter items within each group
const filteredGroupItems = (items: MenuItem[]) => {
  return items.filter(item => hasAccess(item.userTypes))
}
</script>

<template>
  <aside
    :class="[
      'fixed mt-16 flex flex-col lg:mt-0 top-0 px-2 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-99990 border-r border-gray-200',
      {
        'lg:w-(--sidebar-max)': isExpanded || isMobileOpen || isHovered,
        'lg:w-(--sidebar-min)': !isExpanded && !isHovered,
        'translate-x-0 w-(--sidebar-max)': isMobileOpen,
        '-translate-x-full': !isMobileOpen,
        'lg:translate-x-0': true,
      },
    ]"
    @mouseenter="!isExpanded && (isHovered = true)"
    @mouseleave="isHovered = false"
  >
    <div :class="['h-[98px] py-8 hidden lg:flex', !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start']">
      <router-link to="/" class="flex justify-center">
        <img
          v-if="isExpanded || isHovered || isMobileOpen"
          class="dark:hidden"
          src="/images/logo/logo-dark.svg"
          alt="Logo"
          width="80%"
        />
        <img
          v-if="isExpanded || isHovered || isMobileOpen"
          class="hidden dark:block"
          src="/images/logo/logo-light.svg"
          alt="Logo"
          width="80%"
        />
        <template v-else>
          <img class="dark:hidden" src="/images/logo/logo-icon-dark.svg" alt="Logo" width="30" height="30" />
          <img class="hidden dark:block" src="/images/logo/logo-icon-light.svg" alt="Logo" width="30" height="30" />
        </template>
      </router-link>
    </div>

    <!-- Debug: Show user type -->
    <div v-if="(isExpanded || isHovered || isMobileOpen) && userType"
         class="flex justify-center items-center capitalize p-3 text-sm text-white dark:text-secondary absolute bottom-0 left-0 right-0 bg-primary/70 dark:bg-secondary/30">
        {{ userType }} Dashboard
    </div>

    <div class="max-h-[calc(100vh_-_150px)] flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
      <nav class="mb-6">
        <div class="flex flex-col gap-4">
          <!-- Loop through filtered menu groups -->
          <div v-for="(menuGroup, groupIndex) in filteredMenuGroups" :key="groupIndex">
            <h2
              v-if="menuGroup.title"
              :class="[
                'mb-4 text-xs uppercase flex leading-[20px] text-gray-400',
                !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start',
              ]"
            >
              <template v-if="(isExpanded || isHovered || isMobileOpen) && menuGroup.title">
                {{ menuGroup.title }}
              </template>
              <template v-else-if="menuGroup.title">
                <MoreDots />
              </template>
            </h2>
            <ul class="flex flex-col gap-3">
              <!-- Loop through filtered items in each group -->
              <li v-for="(item, index) in filteredGroupItems(menuGroup.items)" :key="item.name">
                <button
                  v-if="item.subItems"
                  @click="toggleSubmenu(groupIndex, index)"
                  :class="[
                    'menu-item group w-full',
                    {
                      'menu-item-active': isSubmenuOpen(groupIndex, index),
                      'menu-item-inactive': !isSubmenuOpen(groupIndex, index),
                    },
                    !isExpanded && !isHovered ? 'lg:justify-center' : 'lg:justify-start',
                  ]"
                >
                  <span
                    :class="[
                      isSubmenuOpen(groupIndex, index)
                        ? 'menu-item-icon-active'
                        : 'menu-item-icon-inactive',
                    ]"
                  >
                    <component :is="item.icon" class="fill-current w-6 h-6" />
                  </span>
                  <span class="menu-item-text" :class="{ 'sidebar-expanded': isExpanded || isHovered || isMobileOpen }">
                    {{ item.name }}
                  </span>
                  <ChevronDownIcon
                    v-if="isExpanded || isHovered || isMobileOpen"
                    :class="[
                      'ml-auto w-5 h-5 transition-transform duration-200',
                      { 'rotate-180 text-primary': isSubmenuOpen(groupIndex, index) },
                    ]"
                  />
                </button>
                <router-link
                  v-else-if="item.path"
                  :to="item.path"
                  :class="[
                    'menu-item group',
                    {
                      'menu-item-active': isActive(item.path),
                      'menu-item-inactive': !isActive(item.path),
                    },
                    !isExpanded && !isHovered ? 'lg:justify-center' : 'lg:justify-start',
                  ]"
                >
                  <component :is="item.icon" class="w-6 h-6" />
                  <span class="menu-item-text" :class="{ 'sidebar-expanded': isExpanded || isHovered || isMobileOpen }">
                    {{ item.name }}
                  </span>
                </router-link>
                <transition>
                  <div
                    v-show="
                      isSubmenuOpen(groupIndex, index) && (isExpanded || isHovered || isMobileOpen)
                    "
                  >
                    <ul class="mt-2 space-y-1 ml-9">
                      <li v-for="subItem in item.subItems" :key="subItem.name">
                        <!-- Check subItem user type access -->
                        <template v-if="hasAccess(subItem.userTypes)">
                          <router-link
                            :to="subItem.path"
                            :class="[
                              'menu-dropdown-item',
                              {
                                'menu-dropdown-item-active': isActive(subItem.path),
                                'menu-dropdown-item-inactive': !isActive(subItem.path),
                              },
                            ]"
                          >
                            {{ subItem.name }}
                            <span class="flex items-center gap-1 ml-auto">
                              <span
                                v-if="subItem.new"
                                :class="[
                                  'menu-dropdown-badge',
                                  {
                                    'menu-dropdown-badge-active': isActive(subItem.path),
                                    'menu-dropdown-badge-inactive': !isActive(subItem.path),
                                  },
                                ]"
                              >
                                new
                              </span>
                              <span
                                v-if="subItem.pro"
                                :class="[
                                  'menu-dropdown-badge',
                                  {
                                    'menu-dropdown-badge-active': isActive(subItem.path),
                                    'menu-dropdown-badge-inactive': !isActive(subItem.path),
                                  },
                                ]"
                              >
                                Assnd
                              </span>
                            </span>
                          </router-link>
                        </template>
                      </li>
                    </ul>
                  </div>
                </transition>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </div>
  </aside>
</template>
